/**
 * disposableIdentity.js — identità USA-E-GETTA per la "Massima Sicurezza" (Tor).
 *
 * Un'identità effimera, SOLO CHIAVI, niente PIN/backup: nasce al volo, si prende
 * un @sigil casuale, si registra via `spawnDisposable` (modello A: autorizzata dal
 * tuo account già stabilito, senza legame salvato principale↔usa-e-getta), fa da
 * identità a DUE VIE per la sessione, e si autodistrugge a fine sessione.
 *
 * Ciclo di vita:
 *  - creaUsaeGetta()   → (autenticato come TU main) spawn + auth come usa-e-getta
 *                        + pubblica chiavi/prekey (raggiungibile) + persiste la
 *                        mnemonica effimera in sessionStorage (sopravvive al reload
 *                        di ingresso in modalità Tor, NON al riavvio app).
 *  - riattivaDaSessione() → dopo il reload: ri-deriva le chiavi dalla mnemonica
 *                        persistita e ri-autentica come usa-e-getta (Fetta 2).
 *  - distruggiUsaeGetta() → tombMySigil (autenticati come lei, chiavi in RAM/sess)
 *                        + pulizia locale. Chiamata a Tor-off e prima di uscire.
 *  - pulisciAllAvvio()  → al boot rimuove eventuali usa-e-getta rimaste dal
 *                        catalogo locale (l'app killata non ha potuto distruggere);
 *                        lato server ci pensa il TTL (expiresAt 24h).
 *
 * ⚠️ La mnemonica in sessionStorage è il segreto dell'usa-e-getta: accettabile
 * perché è throwaway e si cancella a fine sessione; la mnemonica del tuo account
 * PRINCIPALE non è mai qui.
 */

import { newMnemonic, mnemonicToSeed } from './mnemonic';
import { initSodium, keypairFromSeed, boxKeypairFromSeed, toBase64Url, deriveUid } from './crypto';
import { callFn, auth, signOut } from './firebase';
import { signInAsIdentity } from './authIdentity';
import { publishMyKeysFromKeypairs } from './messaging';
import { ensureMyPrekeysPublished } from './fsMessaging';
import { removeAccount, saveLastSigil, loadLastSigil, loadAccounts } from './pinCrypto';
import { useStore } from './store';

const SESS_MNEMONIC = 'nullum.disposable.mnemonic';
const SESS_SIGIL = 'nullum.disposable.sigil';

const WORDS = ['ombra', 'eco', 'nebbia', 'fumo', 'vento', 'cifra', 'muto', 'velo', 'nodo', 'onda', 'lampo', 'brina'];
function randomSigil() {
  const w = WORDS[Math.floor(Math.random() * WORDS.length)];
  const suf = Array.from(crypto.getRandomValues(new Uint8Array(3)))
    .map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 5);
  return `${w}_${suf}`;
}

function ss(key, val) {
  try { if (val === undefined) return sessionStorage.getItem(key); sessionStorage.setItem(key, val); return val; }
  catch { return null; }
}
function ssDel(key) { try { sessionStorage.removeItem(key); } catch { /* no-op */ } }

/** @returns {string|null} il sigil usa-e-getta corrente (se in sessione). */
export function currentDisposable() { return ss(SESS_SIGIL); }
export function hasDisposableSession() { return !!ss(SESS_MNEMONIC); }

async function keysFromMnemonic(mnemonic) {
  await initSodium();
  const seed = mnemonicToSeed(mnemonic);
  const signKp = await keypairFromSeed(seed);
  const boxKp = await boxKeypairFromSeed(seed);
  return { signKp, boxKp };
}

/**
 * Crea (server-side) un'identità usa-e-getta. DEVE essere chiamata mentre sei
 * autenticato come il tuo account principale, con Tor SPENTO (diretto a Google:
 * spawnDisposable non passerebbe da Tor). NON cambia l'auth e NON pubblica chiavi:
 * l'attivazione (auth come usa-e-getta + pubblicazione) avviene dopo il reload di
 * ingresso in Tor, in hydrateDisposableSession. Persiste la mnemonica effimera.
 * @returns {Promise<{ sigil: string }>}
 */
export async function creaUsaeGetta() {
  const mnemonic = newMnemonic(); // generata e mai mostrata/salvata come backup
  const { signKp } = await keysFromMnemonic(mnemonic);
  const pubSignB64 = await toBase64Url(signKp.publicKey);

  // Autorizza la creazione DAL main (autenticato ora). Retry se il nome è preso.
  let sigil = randomSigil();
  let created = false;
  for (let i = 0; i < 6 && !created; i++) {
    try {
      const r = await callFn('spawnDisposable')({ sigil, pubKeySign: pubSignB64 });
      if (r?.data?.ok) created = true;
    } catch (e) {
      const m = String(e?.message || '');
      if (m.includes('SIGIL_TAKEN') || m.includes('already-exists')) { sigil = randomSigil(); continue; }
      throw e;
    }
  }
  if (!created) throw new Error('DISPOSABLE_SPAWN_FAILED');

  // ISOLAMENTO: persisti SOLO in sessionStorage (sopravvive al reload di ingresso
  // Tor, si cancella a fine sessione). NON tocchiamo `saveLastSigil` né il catalogo
  // (`addAccount`): l'usa-e-getta non deve MAI diventare l'identità attiva del
  // device né entrare nel selettore — così non può danneggiare il tuo account.
  ss(SESS_MNEMONIC, mnemonic);
  ss(SESS_SIGIL, sigil);
  return { sigil };
}

/**
 * Annulla l'usa-e-getta appena creata SENZA cambiare auth (per il caso "Tor non
 * è partito": resti sul tuo account principale). Non fa tomb (niente auth della
 * usa-e-getta): il sigil creato scade lato server via TTL. Pulisce solo il locale.
 */
export function abortUsaeGetta() {
  ssDel(SESS_MNEMONIC);
  ssDel(SESS_SIGIL);
}

/**
 * Dopo il reload di ingresso in modalità Tor: ri-deriva le chiavi dalla mnemonica
 * persistita e ri-autentica come usa-e-getta. Ritorna null se non c'è sessione.
 */
export async function riattivaDaSessione() {
  const mnemonic = ss(SESS_MNEMONIC);
  const sigil = ss(SESS_SIGIL);
  if (!mnemonic || !sigil) return null;
  const { signKp, boxKp } = await keysFromMnemonic(mnemonic);
  // Al boot Firebase Auth auto-ripristina la sessione del MAIN (IndexedDB): fare
  // signInWithCustomToken DURANTE quel ripristino, in modalità emulator+relay, dava
  // `internal`. Prima azzeriamo la sessione, poi entriamo pulito come usa-e-getta.
  try { await signOut(auth()); } catch { /* nessuna sessione da chiudere: ok */ }
  await signInAsIdentity({ signPub: signKp.publicKey, signSec: signKp.secretKey });
  // ISOLAMENTO: NON `saveLastSigil` — l'usa-e-getta vive solo in store+sessionStorage.
  return { sigil, signKp, boxKp };
}

/**
 * Dopo il reload di ingresso in modalità Tor: riattiva l'usa-e-getta E idrata lo
 * store (identità/chiavi/sigil) così `HomeRoute` va all'inbox come usa-e-getta,
 * senza passare dal login del tuo account principale. Ritorna il sigil o null.
 */
export async function hydrateDisposableSession() {
  console.log('[USAEGETTA] hydrate: riattivo la sessione…', { hasMnem: !!ss(SESS_MNEMONIC), sigil: ss(SESS_SIGIL) });
  const r = await riattivaDaSessione();
  if (!r) { console.log('[USAEGETTA] hydrate: nessuna sessione (mnemonica/sigil mancanti)'); return null; }
  const { sigil, signKp, boxKp } = r;
  console.log('[USAEGETTA] riattivata (auth ok) @' + sigil);
  // Ora siamo autenticati come l'usa-e-getta, attraverso il relay+Tor → rendila
  // raggiungibile a due vie: pubblica chiavi (box) + prekey (FS).
  try { await publishMyKeysFromKeypairs(signKp.publicKey, boxKp.publicKey); console.log('[USAEGETTA] chiavi pubblicate'); } catch (e) { console.warn('[USAEGETTA] publishKeys FALLITA', e?.message || e); }
  ensureMyPrekeysPublished({ signKeypair: { pub: signKp.publicKey, priv: signKp.secretKey }, sigil }).catch(() => { /* no-op */ });
  const uid = await deriveUid(signKp.publicKey);
  const mnemonic = ss(SESS_MNEMONIC);
  const st = useStore.getState();
  st.setIdentity({ publicKey: signKp.publicKey, secretKey: signKp.secretKey, uid, mnemonic });
  st.setBoxKeys({ boxPub: boxKp.publicKey, boxSec: boxKp.secretKey });
  st.setSigil(sigil);
  try { st.setFirebaseUser(auth().currentUser); } catch { /* no-op */ }
  console.log('[USAEGETTA] store idratato: sigil=@' + sigil + ' uid=' + uid);
  return sigil;
}

/**
 * Distrugge l'usa-e-getta: la cancella server-side (tombMySigil, autenticati come
 * lei) + pulisce il locale. Va chiamata a Tor-off / prima di uscire dalla sessione.
 */
export async function distruggiUsaeGetta() {
  const sigil = ss(SESS_SIGIL);
  if (!sigil) return;
  // Assicura di essere autenticati COME l'usa-e-getta (se la chiamiamo dopo un
  // reload potremmo non esserlo): ri-deriva dalle chiavi persistite.
  try { await riattivaDaSessione(); } catch { /* proviamo comunque il tomb */ }
  try { await callFn('tombMySigil')({ sigil }); } catch (e) { console.warn('tomb disposable', e); }
  ssDel(SESS_MNEMONIC);
  ssDel(SESS_SIGIL);
  // Chiudi la sessione Firebase dell'usa-e-getta: così all'uscita (reload) non
  // viene auto-ripristinata un'identità ormai tombata, e riparte pulito il main.
  try { await signOut(auth()); } catch { /* no-op */ }
}

/**
 * Pulizia al boot: se in catalogo restano usa-e-getta (app killata senza
 * distruzione pulita) e NON c'è una sessione attiva, toglile dal catalogo locale.
 * Lato server ci pensa il TTL (expiresAt). Non tocca l'account principale.
 */
export function pulisciAllAvvio() {
  try {
    if (hasDisposableSession()) return; // sessione ancora viva: non toccare
    // Rimuovi eventuali usa-e-getta residue dal catalogo (marcate disposable).
    for (const a of loadAccounts()) {
      if (a?.disposable) { try { removeAccount(a.sigil); } catch { /* no-op */ } }
    }
    // AUTO-RECUPERO (robusto): se l'identità ATTIVA non è (più) nel catalogo —
    // orfana perché era un'usa-e-getta poi rimossa (anche per nome, senza il flag
    // `disposable`) — rimetti attiva la prima identità del catalogo. Così il tuo
    // account principale torna sbloccabile invece di "backup non presente".
    const active = (loadLastSigil() || '').toLowerCase();
    const accts = loadAccounts();
    const inCatalog = accts.some((a) => a.sigil && a.sigil.toLowerCase() === active);
    if (active && !inCatalog && accts.length > 0) saveLastSigil(accts[0].sigil);
  } catch { /* no-op */ }
}
